// Knowledge reads and commands on the cosmos API (p1e): a knowledge base's documents and
// generations, retry, refresh and delete; uploads over XHR (api/send-file.ts, p1h-web-5b).
import {
  Deleting,
  GenerationView,
  isProblem,
  KbDocument,
  MAX_DOCUMENT_BYTES,
  ProblemError,
  parseAnswer,
  Queued,
  Refreshing,
  Uploaded,
} from "@cosmosai/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { z } from "zod";
import { useRuntimeConfig } from "../app/config-context";
import { useAuth } from "../features/auth/auth-context";
import { useCosmos } from "./cosmos";
import { read } from "./read";
import { sendFile } from "./send-file";
import { reauthenticate } from "./unauthorized";

/** Keys under ["cosmos", workspace], so a workspace's refresh reaches them too. */
export const knowledgeKeys = {
  kb: (workspace: string, project: string, kb: string) =>
    ["cosmos", workspace, "kb", project, kb] as const,
  documents: (workspace: string, project: string, kb: string) =>
    ["cosmos", workspace, "kb", project, kb, "documents"] as const,
  generations: (workspace: string, project: string, kb: string) =>
    ["cosmos", workspace, "kb", project, kb, "generations"] as const,
};

/** Polling every 3 s while something moves (p1h-web-5 D9); never otherwise. */
const POLL_MS = 3000;
const kbPath = (workspace: string, project: string, kb: string) => ({
  path: { workspace, project, kb },
});

export function useDocuments(
  workspace: string,
  project: string,
  kb: string,
  { poll = false }: { poll?: boolean } = {},
) {
  const client = useCosmos();
  return useQuery({
    queryKey: knowledgeKeys.documents(workspace, project, kb),
    queryFn: () =>
      read(
        z.array(KbDocument),
        `GET …/knowledge-bases/${kb}/documents`,
        client.GET(
          "/api/v1/workspaces/{workspace}/projects/{project}/knowledge-bases/{kb}/documents",
          {
            params: kbPath(workspace, project, kb),
          },
        ),
      ),
    refetchInterval: poll ? POLL_MS : false,
  });
}

export function useGenerations(
  workspace: string,
  project: string,
  kb: string,
  { poll = false }: { poll?: boolean } = {},
) {
  const client = useCosmos();
  return useQuery({
    queryKey: knowledgeKeys.generations(workspace, project, kb),
    queryFn: () =>
      read(
        z.array(GenerationView),
        `GET …/knowledge-bases/${kb}/generations`,
        client.GET(
          "/api/v1/workspaces/{workspace}/projects/{project}/knowledge-bases/{kb}/generations",
          { params: kbPath(workspace, project, kb) },
        ),
      ),
    refetchInterval: poll ? POLL_MS : false,
  });
}

/** A command on a knowledge base; its documents and generations are read again after it. */
function useKbCommand<T, R>(
  workspace: string,
  project: string,
  kb: string,
  run: (input: T) => Promise<R>,
) {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSettled: () =>
      queries.invalidateQueries({ queryKey: knowledgeKeys.kb(workspace, project, kb) }),
  });
}

export function useDeleteDocument(workspace: string, project: string, kb: string) {
  const client = useCosmos();
  return useKbCommand(workspace, project, kb, (document: string) =>
    read(
      Deleting,
      `DELETE …/knowledge-bases/${kb}/documents/{document}`,
      client.DELETE(
        "/api/v1/workspaces/{workspace}/projects/{project}/knowledge-bases/{kb}/documents/{document}",
        { params: { path: { workspace, project, kb, document } } },
      ),
    ),
  );
}

/** Retries one document's failed ingestions, or every failed one when `document` is left out. */
export function useRetry(workspace: string, project: string, kb: string) {
  const client = useCosmos();
  return useKbCommand(workspace, project, kb, (document?: string) =>
    read(
      Queued,
      `POST …/knowledge-bases/${kb}/retry`,
      client.POST("/api/v1/workspaces/{workspace}/projects/{project}/knowledge-bases/{kb}/retry", {
        params: kbPath(workspace, project, kb),
        body: document ? { document } : {},
      }),
    ),
  );
}

/** Fetches the knowledge base's URL sources again. */
export function useRefresh(workspace: string, project: string, kb: string) {
  const client = useCosmos();
  return useKbCommand(workspace, project, kb, () =>
    read(
      Refreshing,
      `POST …/knowledge-bases/${kb}/refresh`,
      client.POST(
        "/api/v1/workspaces/{workspace}/projects/{project}/knowledge-bases/{kb}/refresh",
        {
          params: kbPath(workspace, project, kb),
          body: {},
        },
      ),
    ),
  );
}

export interface DocumentUpload {
  file: File;
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

/**
 * Sends one document as the platform takes it (its ADR 0026): the raw bytes, octet-stream,
 * `X-Cosmos-Client`, the name in the query; answers queued, or unchanged when already held.
 */
export function useUploadDocument(workspace: string, project: string, kb: string) {
  const auth = useAuth();
  const { cosmosApiUrl } = useRuntimeConfig();
  return useCallback(
    async ({ file, onProgress, signal }: DocumentUpload): Promise<Uploaded> => {
      if (file.size > MAX_DOCUMENT_BYTES) {
        throw new ProblemError({
          status: 413,
          title: "Content too large",
          code: "document.too-large",
          detail: `'${file.name}' is larger than ${MAX_DOCUMENT_BYTES} bytes`,
        });
      }
      const token = await auth.accessToken();
      const enc = encodeURIComponent;
      const path = `/api/v1/workspaces/${enc(workspace)}/projects/${enc(project)}/knowledge-bases/${enc(kb)}/documents`;
      const { status, text } = await sendFile(
        {
          method: "POST",
          url: `${cosmosApiUrl.replace(/\/$/, "")}${path}?name=${enc(file.name)}`,
          headers: {
            "content-type": "application/octet-stream",
            "x-cosmos-client": "studio",
            ...(token ? { authorization: `Bearer ${token}` } : {}),
          },
        },
        file,
        { ...(onProgress ? { onProgress } : {}), ...(signal ? { signal } : {}) },
      );
      const body = parseJson(text);
      if (status >= 200 && status < 300) {
        return parseAnswer(Uploaded, `POST …/knowledge-bases/${kb}/documents`, body);
      }
      if (status === 401 && token) await reauthenticate(auth);
      if (isProblem(body)) throw new ProblemError(body);
      throw new ProblemError({
        status,
        title: status ? `HTTP ${status}` : "Network error",
        detail: status
          ? "the cosmos API refused the upload"
          : "the cosmos API could not be reached",
      });
    },
    [auth, cosmosApiUrl, workspace, project, kb],
  );
}

function parseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}
